<template lang="pug">
.c-static-page-edit-form
  app-main-content-layout(
    title="Static Page Detail"
    backTo="/manage-content/media/static-pages"
  )
    template(#actions)
      el-button(
        type="danger"
        plain
        @click="handleDeletePage"
        :loading="deletingPage"
      ).has-fai
        fai(type="fal" icon="trash").size-18.me-2
        span.min-w-110.d-inline-block Delete

    .page-builder-container(v-if="staticPage")
      .builder-layout
        // Left Column - Blocks List
        .c-content-section.blocks-sidebar
          .blocks-header
            h3 Page Blocks
            .add-section-inline
              .d-flex.align-items-center
                el-select(
                  v-model="newBlockType"
                  placeholder="Select block type"
                  size="default"
                  style="width: 160px; margin-right: 8px;"
                )
                  el-option(
                    label="Rich Text"
                    value="RichText"
                  )
                  el-option(
                    label="Search Bar"
                    value="SearchBar"
                  )
                  el-option(
                    label="Announcement Bar"
                    value="AnnouncementBar"
                  )
                  el-option(
                    label="Stickers"
                    value="Stickers"
                  )
                  el-option(
                    label="Organizations"
                    value="Organizations"
                  )
                  el-option(
                    label="Collections"
                    value="Collections"
                  )
                  el-option(
                    label="List"
                    value="List"
                  )
                  el-option(
                    label="Question"
                    value="Question"
                  )
                  el-option(
                    label="Banner"
                    value="Banner"
                  )
                el-button(
                  type="primary"
                  size="default"
                  @click="startCreatingNewBlock"
                  :disabled="!newBlockType"
                  style="width: 100px;"
                )
                  i.el-icon-plus.me-1
                  span Add

          // Green Gradient Setting
          .green-gradient-setting(v-if="staticPage")
            el-checkbox(
              v-model="greenGradientEnabled"
              @change="handleGreenGradientToggle"
              :loading="updatingGreenGradient"
              size="default"
            ) Teal Gradient Background

          .blocks-list(v-if="!loading && sortedBlocks && sortedBlocks.length > 0")
            draggable.draggable-blocks-list(
              v-model="draggableBlocks"
              handle=".drag-handle"
              ghost-class="ghost-block"
              item-key="id"
              @start="handleDragStart"
              @end="handleDragEnd"
            )
              template(#item="{ element: block }")
                .block-item(
                  :key="block.id"
                  :class="{ 'active': selectedBlockId === block.id }"
                  :data-type="block.blockType"
                  @click="selectBlock(block)"
                )
                  .drag-handle.cursor-move
                    fai(type="fas" icon="grip-vertical").size-14
                  .block-icon
                    i(:class="getBlockIcon(block.blockType)")
                  .block-content
                    .block-name {{ getBlockTypeDisplayName(block.blockType) }}
                  .block-actions
                    el-button(
                      size="default"
                      type="text"
                      class="action-btn"
                      @click="handleDeleteBlock(block)"
                      :loading="block.id === deletingBlockId"
                    )
                      i.el-icon-delete
                    el-button(
                      size="default"
                      type="text"
                      class="action-btn"
                      @click="handleToggleBlockStatus(block)"
                    )
                      i.el-icon-loading(v-if="block.id === togglingBlockId")
                      i.el-icon-view(
                        v-else
                        :class="{ 'enabled': block.isEnabled, 'disabled': !block.isEnabled }"
                      )

          .loading-state(v-else-if="loading")
            .empty-state
              el-empty(
                description="Loading blocks..."
                :image-size="60"
              )

          .no-blocks(v-else-if="!loading && (!sortedBlocks || sortedBlocks.length === 0)")
            .empty-state
              .empty-icon
                i.el-icon-document-add
              .empty-title No page blocks yet

        // Right Column - Block Editor
        .c-content-section.block-editor
          .editor-content(v-if="selectedBlock || isCreatingNewBlock")
            // Rich Text Editor
            RichTextBlock(
              v-if="isRichTextBlock"
              :key="isCreatingNewBlock ? 'new-richtext-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Search Bar Editor
            SearchBarBlock(
              v-else-if="isSearchBarBlock"
              :key="isCreatingNewBlock ? 'new-searchbar-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Announcement Block Editor
            AnnouncementBlock(
              v-else-if="isAnnouncementBarBlock"
              :key="isCreatingNewBlock ? 'new-announcement-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Stickers Block Editor
            StickersBlock(
              v-else-if="isStickersBlock"
              :key="isCreatingNewBlock ? 'new-stickers-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Organizations Block Editor
            OrganizationsBlock(
              v-else-if="isOrganizationsBlock"
              :key="isCreatingNewBlock ? 'new-organizations-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Collections Block Editor
            CollectionsBlock(
              v-else-if="isCollectionsBlock"
              :key="isCreatingNewBlock ? 'new-collections-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // List Block Editor
            ListBlock(
              v-else-if="isListBlock"
              :key="isCreatingNewBlock ? 'new-list-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Question Block Editor
            QuestionBlock(
              v-else-if="isQuestionBlock"
              :key="isCreatingNewBlock ? 'new-question-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Banner Block Editor
            BannerBlock(
              v-else-if="isBannerBlock"
              :key="isCreatingNewBlock ? 'new-banner-block' : selectedBlock?.id"
              :block="selectedBlock"
              :is-creating="isCreatingNewBlock"
              :page-id="staticPage.id"
              :static-page-data="staticPage"
              @save="handleBlockSave"
              @cancel="cancelNewBlock"
            )

            // Other block types (placeholder for now)
            div(v-else)
              .editor-header
                h3 {{ getBlockTypeDisplayName(selectedBlock.blockType) }} Block
                p This block type editor is not implemented yet.

              .coming-soon
                el-alert(
                  title="Coming Soon"
                  type="info"
                  :closable="false"
                )
                  p
                    | Editor for {{ getBlockTypeDisplayName(selectedBlock.blockType) }}
                    | blocks will be available soon.

          .editor-placeholder(v-else)
            .placeholder-content
              i.el-icon-edit-outline.placeholder-icon
              h3 Select a Block
              p Choose a block from the left panel to edit its content, or add a new section

    .api-error.text-center.my-5(v-if="apiError")
      h3 Error loading static page
      p {{ apiError }}
      el-button(
        @click="getStaticPage"
        class="mt-3"
      ) Retry
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue design system
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-static-page-edit-form {
  padding: 2rem;
  
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// API Error State
.api-error {
  color: #f56c6c;
  padding: 2rem;
  text-align: center;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: $gray-600;
    margin-bottom: 1rem;
  }

  button {
    background-color: #f56c6c;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background-color: #e45656;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
    }
  }
}

/* Page Builder Layout */
.page-builder-container {
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.builder-layout {
  display: flex;
  flex: 1;
  gap: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Blocks Sidebar */
.blocks-sidebar {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

.blocks-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $gray-200;

  h3 {
    margin: 0 0 1rem 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: $gray-900;
  }
}

.add-section-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  .d-flex {
    width: 100%;
    gap: 0.5rem;
  }
}

// Green Gradient Setting
.green-gradient-setting {
  padding: 0.75rem;
  background: $gray-50;
  border-radius: 10px;
  border: 1px solid $gray-200;
  margin-bottom: 1rem;

  :deep(.el-checkbox) {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;

    .el-checkbox__label {
      color: $gray-700;
      font-weight: 500;
    }
  }
}

.blocks-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  overflow-y: auto;
  padding-right: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $gray-100;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: $gray-300;
    border-radius: 3px;

    &:hover {
      background: $gray-400;
    }
  }
}

.block-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: $gray-50;
  border: 1px solid $gray-200;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: color.adjust($color-purple-text, $lightness: 47%);
    border-color: color.adjust($color-purple-text, $lightness: 30%);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.1);
  }

  &.active {
    background: color.adjust($color-purple-text, $lightness: 45%);
    border-color: $color-purple-text;
    box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.15);
  }
}

.block-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  margin-right: 0.75rem;
  flex-shrink: 0;
  border: 1px solid $gray-200;

  i {
    font-size: 16px;
  }
}

.block-content {
  flex: 1;
  min-width: 0;
}

.block-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: $gray-900;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.block-summary {
  font-size: 0.75rem;
  color: $gray-500;
  font-style: italic;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.block-order {
  font-size: 0.75rem;
  color: $gray-400;
}

.block-actions {
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 0.5rem;
}

.block-item:hover .block-actions {
  opacity: 1;
}

.action-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  i {
    font-size: 16px;
    color: $gray-500;
  }

  &:hover {
    background: white;

    i {
      color: $color-purple-text;
    }
  }

  i.enabled {
    color: #67c23a;
  }

  i.disabled {
    color: #f56c6c;
    opacity: 0.7;
  }
}

/* Drag and Drop Styles */
.draggable-blocks-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.drag-handle {
  cursor: move;
  cursor: grab;
  color: $gray-400;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  width: 20px;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: $color-purple-text;
  }

  &:active {
    cursor: grabbing;
  }
}

.ghost-block {
  opacity: 0.5;
  background: color.adjust($color-purple-text, $lightness: 45%) !important;
  border: 2px dashed $color-purple-text !important;
}

/* Block Editor */
.block-editor {
  flex: 1;
  overflow: hidden;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.editor-content {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.editor-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $gray-200;

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: $gray-900;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: $gray-600;
  }
}

.editor-form {
  max-width: 600px;

  :deep(.el-form-item) {
    margin-bottom: 1.5rem;

    .el-form-item__label {
      font-weight: 600;
      color: $gray-700;
      font-size: 0.875rem;
    }
  }
}

.coming-soon {
  text-align: center;
  padding: 3rem 1.5rem;
}

.editor-placeholder {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $gray-50;
}

.placeholder-content {
  text-align: center;
  padding: 3rem;

  .placeholder-icon {
    font-size: 64px;
    color: $gray-300;
    margin-bottom: 1.5rem;
    display: block;
  }

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: $gray-600;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: $gray-400;
  }
}

.no-blocks,
.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.empty-state {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1.5rem;

  .empty-icon {
    margin-bottom: 1.5rem;

    i {
      font-size: 64px;
      color: $gray-300;
    }
  }

  .empty-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: $gray-600;
  }
}

/* Editor Actions */
.editor-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid $gray-200;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

/* Block Type Icons - Refined Color Palette */
.block-item[data-type="HeroSlideshow"] .block-icon {
  background: #fff3e0;
  border-color: #ffe0b2;

  i {
    color: #ff9800;
  }
}

.block-item[data-type="SearchBar"] .block-icon {
  background: #e6f7f6;
  border-color: #b2ebf2;

  i {
    color: #1EBEAF;
  }
}

.block-item[data-type="Collections"] .block-icon {
  background: #f3e5f5;
  border-color: #e1bee7;

  i {
    color: #9c27b0;
  }
}

.block-item[data-type="List"] .block-icon {
  background: #e8f5e8;
  border-color: #c8e6c9;

  i {
    color: #4caf50;
  }
}

.block-item[data-type="RichText"] .block-icon {
  background: #f5f5f5;
  border-color: #e0e0e0;

  i {
    color: #616161;
  }
}

.block-item[data-type="AnnouncementBar"] .block-icon {
  background: #f3e5f5;
  border-color: #e1bee7;

  i {
    color: #673ab7;
  }
}

.block-item[data-type="Question"] .block-icon {
  background: #e6f7f6;
  border-color: #b2ebf2;

  i {
    color: #1EBEAF;
  }
}

.block-item[data-type="WelcomeUser"] .block-icon {
  background: #f3e5f5;
  border-color: #e1bee7;

  i {
    color: #9c27b0;
  }
}

.block-item[data-type="Banner"] .block-icon {
  background: #ffebee;
  border-color: #ffcdd2;

  i {
    color: #f44336;
  }
}

.block-item[data-type="Stickers"] .block-icon {
  background: #fce4ec;
  border-color: #f8bbd0;

  i {
    color: #f7598d;
  }
}

.block-item[data-type="Organizations"] .block-icon {
  background: #fff8e1;
  border-color: #ffecb3;

  i {
    color: #ffc107;
  }
}

/* Element Plus Component Overrides */
:deep(.el-select) {
  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }
  }

  .el-input__inner {
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;
  }
}

:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

:deep(.el-checkbox) {
  .el-checkbox__inner {
    border-radius: 4px;
    border: 1.5px solid $gray-300;
    transition: all 0.2s ease;

    &:hover {
      border-color: $color-purple-text;
    }
  }

  &.is-checked {
    .el-checkbox__inner {
      background-color: $color-purple-text;
      border-color: $color-purple-text;
    }

    .el-checkbox__label {
      color: $gray-900;
    }
  }
}

/* Page Background */
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .builder-layout {
    flex-direction: column;
    gap: 1.5rem;
  }

  .blocks-sidebar {
    width: 100%;
  }

  .blocks-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0.75rem;
  }
}

@media (max-width: 768px) {
  .c-static-page-edit-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  .blocks-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .c-static-page-edit-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>

<script>
import {
  onMounted, defineComponent, ref, computed,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import draggable from 'vuedraggable';
import shared from '../shared/index.ts';
import useStaticPageUtils from './shared/index.ts';
import { mutateStaticPage } from '@/graphs/definitions';
import RichTextBlock from './blocks/RichTextBlock.vue';
import SearchBarBlock from './blocks/SearchBarBlock.vue';
import AnnouncementBlock from './blocks/AnnouncementBlock.vue';
import StickersBlock from './blocks/StickersBlock.vue';
import OrganizationsBlock from './blocks/OrganizationsBlock.vue';
import ListBlock from './blocks/ListBlock.vue';
import QuestionBlock from './blocks/QuestionBlock.vue';
import BannerBlock from './blocks/BannerBlock.vue';
import CollectionsBlock from './blocks/CollectionsBlock.vue';

export default defineComponent({
  name: 'ManageContent_Media_StaticPageDetail',
  components: {
    RichTextBlock,
    SearchBarBlock,
    AnnouncementBlock,
    StickersBlock,
    OrganizationsBlock,
    CollectionsBlock,
    ListBlock,
    QuestionBlock,
    BannerBlock,
    draggable,
  },
  setup() {
    const { loading, removeItemGetConfirm, makeGraphRequest, getAuthToken } = shared();
    const {
      processStaticPageBlocks,
      getBlockTypeColor,
      getBlockTypeDisplayName,
      fetchStaticPage,
      isValidBlock,
      getBlockSummary,
      deleteStaticPageBlock,
      deleteStaticPage,
      toggleStaticPageBlockStatus,
      updateBlockOrder,
    } = useStaticPageUtils();

    const route = useRoute();
    const router = useRouter();
    const staticPage = ref(null);
    const apiError = ref(null);
    const selectedBlockId = ref(null);
    const selectedBlock = ref(null);
    const deletingBlockId = ref(null);
    const togglingBlockId = ref(null);
    const deletingPage = ref(false);
    const updatingGreenGradient = ref(false);

    // New block creation
    const newBlockType = ref('RichText'); // Default to RichText
    const isCreatingNewBlock = ref(false);

    // Optimistic updates for drag and drop
    const optimisticBlocks = ref(null);

    // Computed property to get processed and sorted blocks
    const sortedBlocks = computed(() => {
      if (!staticPage.value) {
        return [];
      }

      // Use optimistic blocks if available (during drag and drop)
      if (optimisticBlocks.value) {
        return optimisticBlocks.value;
      }

      return processStaticPageBlocks(staticPage.value);
    });

    // Draggable blocks - reactive copy for drag and drop
    const draggableBlocks = computed({
      get() {
        return sortedBlocks.value;
      },
      set() {
        // This will be handled by the drag end event
      },
    });

    // Computed property to check if current block is a rich text block
    const isRichTextBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'RichText')
      || (isCreatingNewBlock.value && newBlockType.value === 'RichText'));

    // Computed property to check if current block is a search bar block
    const isSearchBarBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'SearchBar')
      || (isCreatingNewBlock.value && newBlockType.value === 'SearchBar'));

    // Computed property to check if current block is an announcement bar block
    const isAnnouncementBarBlock = computed(() => (selectedBlock.value
      && (selectedBlock.value.blockType === 'AnnouncementBar' || selectedBlock.value.blockType === 'Announcement'))
      || (isCreatingNewBlock.value && newBlockType.value === 'AnnouncementBar'));

    // Computed property to check if current block is a stickers block
    const isStickersBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'Stickers')
      || (isCreatingNewBlock.value && newBlockType.value === 'Stickers'));

    // Computed property to check if current block is an organizations block
    const isOrganizationsBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'Organizations')
      || (isCreatingNewBlock.value && newBlockType.value === 'Organizations'));

    // Computed property to check if current block is a collections block
    const isCollectionsBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'Collections')
      || (isCreatingNewBlock.value && newBlockType.value === 'Collections'));

    // Computed property to check if current block is a list block
    const isListBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'List')
      || (isCreatingNewBlock.value && newBlockType.value === 'List'));

    // Computed property to check if current block is a question block
    const isQuestionBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'Question')
      || (isCreatingNewBlock.value && newBlockType.value === 'Question'));

    // Computed property to check if current block is a banner block
    const isBannerBlock = computed(() => (selectedBlock.value && selectedBlock.value.blockType === 'Banner')
      || (isCreatingNewBlock.value && newBlockType.value === 'Banner'));

    // Computed property for green gradient enabled checkbox
    const greenGradientEnabled = computed({
      get() {
        return staticPage.value?.greenGradientEnabled || false;
      },
      set(value) {
        if (staticPage.value) {
          staticPage.value.greenGradientEnabled = value;
        }
      },
    });

    // Get icon class for block type
    function getBlockIcon(blockType) {
      const iconMap = {
        HeroSlideshow: 'el-icon-picture',
        SearchBar: 'el-icon-search',
        Collections: 'el-icon-collection',
        List: 'el-icon-s-order',
        RichText: 'el-icon-document',
        AnnouncementBar: 'el-icon-bell',
        Question: 'el-icon-question',
        WelcomeUser: 'el-icon-user',
        Banner: 'el-icon-picture-outline',
        Stickers: 'el-icon-collection-tag',
        Organizations: 'el-icon-office-building',
      };
      return iconMap[blockType] || 'el-icon-document';
    }

    // Handle block selection
    function selectBlock(block) {
      selectedBlockId.value = block.id;
      selectedBlock.value = block;
      isCreatingNewBlock.value = false;
    }

    async function getStaticPage() {
      try {
        loading.value = true;
        apiError.value = null;

        // Use the utility function for fetching
        staticPage.value = await fetchStaticPage(route.params.id, true);

        // Auto-select first Rich Text block if available
        const firstRichTextBlock = sortedBlocks.value.find(
          (block) => block.blockType === 'RichText',
        );
        if (firstRichTextBlock) {
          selectBlock(firstRichTextBlock);
        }
      } catch (err) {
        console.error('Error fetching static page:', err);
        apiError.value = err.message || 'Error loading static page. Please try again later.';
      } finally {
        loading.value = false;
      }
    }

    // Refresh static page data without showing loading state (prevents flicker)
    async function refreshStaticPageData() {
      try {
        apiError.value = null;

        // Use the utility function for fetching
        staticPage.value = await fetchStaticPage(route.params.id, true);
      } catch (err) {
        console.error('Error refreshing static page:', err);
        apiError.value = err.message || 'Error loading static page. Please try again later.';
      }
    }

    // Handle block deletion with confirmation
    function handleDeleteBlock(block) {
      removeItemGetConfirm(async () => {
        try {
          deletingBlockId.value = block.id;
          await deleteStaticPageBlock(block.id, staticPage.value.id);

          // Refresh the static page data without showing loading state (prevents flicker)
          await refreshStaticPageData();

          // Clear selection if the deleted block was selected
          if (selectedBlockId.value === block.id) {
            selectedBlockId.value = null;
            selectedBlock.value = null;
          }
        } catch (error) {
          console.error('Error deleting block:', error);
        } finally {
          deletingBlockId.value = null;
        }
      });
    }

    // Handle page deletion with confirmation
    function handleDeletePage() {
      removeItemGetConfirm(async () => {
        try {
          deletingPage.value = true;
          await deleteStaticPage(staticPage.value.id);

          // Redirect to static pages list after successful deletion
          router.push('/manage-content/media/static-pages');
        } catch (error) {
          console.error('Error deleting page:', error);
          // Error handling is done by the confirmation dialog
        } finally {
          deletingPage.value = false;
        }
      });
    }

    // Handle block status toggle
    async function handleToggleBlockStatus(block) {
      try {
        togglingBlockId.value = block.id;
        await toggleStaticPageBlockStatus(staticPage.value.id, block.id);

        // Refresh the static page data without showing loading state (prevents flicker)
        await refreshStaticPageData();
      } catch (error) {
        console.error('Error toggling block status:', error);
      } finally {
        togglingBlockId.value = null;
      }
    }

    // Handle green gradient toggle
    async function handleGreenGradientToggle(value) {
      try {
        updatingGreenGradient.value = true;

        const inputData = {
          id: staticPage.value.id,
          title: staticPage.value.title,
          slug: staticPage.value.slug,
          greenGradientEnabled: value,
        };

        const response = await makeGraphRequest(
          'mutation',
          mutateStaticPage,
          { input: inputData },
          getAuthToken(),
        );

        if (response && response.data && response.data.mutateStaticPage) {
          // Update local state with response data
          staticPage.value.greenGradientEnabled = response.data.mutateStaticPage.greenGradientEnabled;
        }
      } catch (error) {
        console.error('Error updating green gradient setting:', error);
        // Revert the checkbox state on error
        if (staticPage.value) {
          staticPage.value.greenGradientEnabled = !value;
        }
      } finally {
        updatingGreenGradient.value = false;
      }
    }

    // Handle canceling new block creation
    function cancelNewBlock() {
      isCreatingNewBlock.value = false;

      // Auto-select first Rich Text block if available
      const firstRichTextBlock = sortedBlocks.value.find(
        (block) => block.blockType === 'RichText',
      );
      if (firstRichTextBlock) {
        selectBlock(firstRichTextBlock);
      }
    }

    // Handle starting new block creation
    function startCreatingNewBlock() {
      isCreatingNewBlock.value = true;
      selectedBlockId.value = null;
      selectedBlock.value = null;
    }

    // Handle block save from component
    async function handleBlockSave(savedBlock) {
      try {

        // Exit creation mode
        cancelNewBlock();

        // Refresh the page data without showing loading state (prevents flicker)
        await refreshStaticPageData();
      } catch (error) {
        console.error('Error handling block save:', error);
      }
    }

    // Handle drag start
    function handleDragStart() {
    }

    // Handle drag end - update only the moved block's order
    async function handleDragEnd(evt) {
      try {

        // Get the moved block and its new position
        const { oldIndex, newIndex } = evt;

        if (oldIndex === newIndex) {
          // No actual movement occurred
          return;
        }

        // Get the original blocks order before optimistic update
        const originalBlocks = processStaticPageBlocks(staticPage.value);
        const movedBlock = originalBlocks[oldIndex];

        // Backend expects 0-based indexing for sortOrder
        const newSortOrder = newIndex;


        // Optimistic update: immediately show the new order
        const optimisticBlocksArray = [...originalBlocks];
        const [removedBlock] = optimisticBlocksArray.splice(oldIndex, 1);
        optimisticBlocksArray.splice(newIndex, 0, removedBlock);
        optimisticBlocks.value = optimisticBlocksArray;

        try {
          // Update only the moved block - backend will handle reordering others
          await updateBlockOrder(staticPage.value.id, movedBlock, newSortOrder);

          // Refresh the page data to get the actual new order from backend
          await refreshStaticPageData();

          // Clear optimistic state after successful update
          optimisticBlocks.value = null;
        } catch (updateError) {
          // Revert optimistic update on error
          optimisticBlocks.value = null;
          console.error('Error updating block order, reverted:', updateError);
          // Optionally show an error message to the user
        }
      } catch (error) {
        console.error('Error in drag end handler:', error);
        // Clear optimistic state on any error
        optimisticBlocks.value = null;
      }
    }

    onMounted(async () => {
      await getStaticPage();
    });

    return {
      staticPage,
      loading,
      apiError,
      getStaticPage,
      sortedBlocks,
      draggableBlocks,
      getBlockTypeColor,
      getBlockTypeDisplayName,
      isValidBlock,
      getBlockSummary,
      getBlockIcon,
      selectedBlockId,
      selectedBlock,
      selectBlock,
      handleDeleteBlock,
      deletingBlockId,
      togglingBlockId,
      handleToggleBlockStatus,
      handleDeletePage,
      deletingPage,
      newBlockType,
      isCreatingNewBlock,
      isRichTextBlock,
      isSearchBarBlock,
      isAnnouncementBarBlock,
      isStickersBlock,
      isOrganizationsBlock,
      isCollectionsBlock,
      isListBlock,
      isQuestionBlock,
      isBannerBlock,
      startCreatingNewBlock,
      cancelNewBlock,
      handleBlockSave,
      handleDragStart,
      handleDragEnd,
      optimisticBlocks,
      greenGradientEnabled,
      updatingGreenGradient,
      handleGreenGradientToggle,
    };
  },
});
</script>
